// src/feature-module/settings/keymanagement/Add Keys/ViewKeyModule.tsx
import React, { useMemo } from 'react';
import { Modal, Button, Badge } from 'react-bootstrap';

export interface KeyDTO {
  id: string | number;
  keyCode?: string | null;
  noOfKeys?: number | null;
  separatedKeys?: number | null;
  bundles?: { count: number }[] | number | null;
  responsibleParty?: string | null;
  isRestricted?: boolean | null;
  handoverStatus?: string | null;
  status?: string | null;
  createdBy?: string | null;
  createdAt?: string | null;
  updatedBy?: string | null;
  updatedAt?: string | null;

  doorId?: string | null;
  roomId?: string | null;
  roomName?: string | null;
  floorId?: string | null;
  floorName?: string | null;
  buildingId?: string | null;
  buildingName?: string | null;

  vehicleId?: string | null;
  vehicleNumberPlate?: string | null;

  [k: string]: any;
}

interface Props {
  show: boolean;
  onHide: () => void;
  keyData: KeyDTO | null;
}

const formatDateTime = (iso?: string | null) =>
  iso ? new Date(iso).toLocaleString() : '';

const statusVariant = (s?: string | null) =>
  s === 'ACTIVE' ? 'success' : s === 'INACTIVE' ? 'secondary' : 'info';

const isBlank = (v: unknown) => {
  if (v === null || v === undefined) return true;
  if (typeof v === 'string') return v.trim().length === 0;
  return false; // numbers/booleans/ReactNodes considered present
};

const ViewKeyModule: React.FC<Props> = ({ show, onHide, keyData }) => {
  // compute bundles total; null means "no value -> hide"
  const totalBundles: number | null = useMemo(() => {
    if (!keyData || keyData.bundles === null || keyData.bundles === undefined) return null;
    if (Array.isArray(keyData.bundles)) {
      return keyData.bundles.reduce((sum, b) => sum + (b?.count || 0), 0);
    }
    if (typeof keyData.bundles === 'number') return keyData.bundles;
    return null;
  }, [keyData]);

  // Build visible fields only (skip null/empty)
  const fields = useMemo(() => {
    const out: { label: string; value: React.ReactNode }[] = [];
    if (!keyData) return out;

    const pushIf = (label: string, value: any, checkRaw: any = value) => {
      if (!isBlank(checkRaw)) out.push({ label, value });
    };

    pushIf('ID', keyData.id, keyData.id);
    pushIf('Key Code', keyData.keyCode, keyData.keyCode);
    pushIf('Total Keys', keyData.noOfKeys, keyData.noOfKeys);
    pushIf('Separated Keys', keyData.separatedKeys, keyData.separatedKeys);
    pushIf('Bundles', totalBundles, totalBundles);

    pushIf('Responsible Party', keyData.responsibleParty, keyData.responsibleParty);

    // booleans: show only if not null/undefined (false is valid and should show)
    if (keyData.isRestricted !== null && keyData.isRestricted !== undefined) {
      out.push({ label: 'Restricted', value: keyData.isRestricted ? 'Yes' : 'No' });
    }

    pushIf('Handover Status', keyData.handoverStatus, keyData.handoverStatus);

    if (!isBlank(keyData.status)) {
      out.push({
        label: 'Status',
        value: <Badge bg={statusVariant(keyData.status)}>{keyData!.status}</Badge>,
      });
    }

    // Location/vehicle
    pushIf('Building', keyData.buildingName, keyData.buildingName);
    pushIf('Floor', keyData.floorName, keyData.floorName);
    pushIf('Room', keyData.roomName, keyData.roomName);
    pushIf('Vehicle', keyData.vehicleNumberPlate, keyData.vehicleNumberPlate);

    // Audit
    const createdAtText = formatDateTime(keyData.createdAt);
    if (!isBlank(createdAtText)) out.push({ label: 'Created At', value: createdAtText });
    pushIf('Created By', keyData.createdBy, keyData.createdBy);

    const updatedAtText = formatDateTime(keyData.updatedAt);
    if (!isBlank(updatedAtText)) out.push({ label: 'Updated At', value: updatedAtText });
    pushIf('Updated By', keyData.updatedBy, keyData.updatedBy);

    return out;
  }, [keyData, totalBundles]);

  return (
    <Modal show={show} onHide={onHide} centered size="lg">
      <Modal.Header closeButton>
        <Modal.Title>View Key</Modal.Title>
      </Modal.Header>

      <Modal.Body>
        {!keyData ? (
          <div className="text-muted">No data.</div>
        ) : fields.length === 0 ? (
          <div className="text-muted">No non-empty fields to display.</div>
        ) : (
          <div className="row row-cols-1 row-cols-md-2 row-cols-lg-3 g-3">
            {fields.map((f, idx) => (
              <div className="col" key={idx}>
                <div className="border rounded p-3 h-100">
                  <div className="text-muted small">{f.label}</div>
                  <div className="fw-semibold mt-1">{f.value as any}</div>
                </div>
              </div>
            ))}
          </div>
        )}
      </Modal.Body>

      <Modal.Footer>
        <Button variant="primary" onClick={onHide}>Close</Button>
      </Modal.Footer>
    </Modal>
  );
};

export default ViewKeyModule;
